import React, { useRef, useCallback, useMemo, useState, useEffect } from "react";
import { useParams, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import moment from "moment";
import momentDurationFormatSetup from "moment-duration-format";
import DocumentMeta from "react-document-meta";
import { toPng } from "html-to-image";
import { Menu, Item, useContextMenu, Submenu } from "react-contexify";
import { parseInt } from "lodash";
import "react-contexify/dist/ReactContexify.css";
import "./styles/credits.css";
import "./styles/big-timer.css";
import "./styles/image-with-message.css";

import DrawOnScreen from "./output/DrawOnScreen";
import transparentImage from "./img/transparent.jpg";
import OutputCueTransition from "./output/OutputCueTransition";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck } from "@fortawesome/free-solid-svg-icons";
import { socketActions } from "../imports";

momentDurationFormatSetup(moment);

function useQuery() {
	const { search } = useLocation();

	return useMemo(() => new URLSearchParams(search), [search]);
}

export default function Output({
	width = "screen",
	height = "screen",
	cloud = false,
	cloudGraphics = [],
	order = [],
	projectName = "",
}) {
	const { projectId, outputId = 1 } = useParams();
	const [mirrorV, setMirrorV] = useState(false)
	const [mirrorH, setMirrorH] = useState(false)
	const [internalBg, setInternalBg] = useState('')

	const [forceMute, setForceMute] = useState(false)

	let query = useQuery();
	const { show } = useContextMenu({
		id: "output",
	});

	const isPreview = useLocation().pathname.includes("/preview/");

	const settings = useSelector((state) => state.main?.settings);
	const selectedProject = useSelector(
		(state) => state?.main?.projects?.[projectId]
	);
	const meta = {
		title: `${isPreview ? "Preview" : "Output"} ${outputId} - ${
			selectedProject?.name || projectName
		} `,
	};

	const showContextMenu = (e) => {
		if (["none"].includes(settings?.output_cursor_show)) return null;
		show(e);
	};

	const pngDownloadRef = useRef();

	const savePNG = useCallback(() => {
		if (pngDownloadRef.current === null) {
			return;
		}

		toPng(pngDownloadRef.current, { cacheBust: true })
			.then((dataUrl) => {
				const link = document.createElement("a");
				link.download = cloud
					? "gfx-live-save.png.png"
					: "h2r-graphics-save.png.png";
				link.type = "image/png";
				link.href = dataUrl;
				link.click();
			})
			.catch((err) => {
				console.log(err);
			});
	}, [pngDownloadRef]);

	useEffect(()=>{
		if(query.get("muted")) {
			console.log('this one is muted')
			setForceMute(true)
		}
	}, [query])

	const handleItemClick = ({ event, props, triggerEvent, data }) => {
		switch (event.currentTarget.id) {
			case "save_png":
				setTimeout(() => {
					savePNG();
				}, 500);
				break;

			case "mute":
				setForceMute(!forceMute)
				break;

			case "mirror":
				if(data === 'reset') {
					setMirrorH(false)
					return setMirrorV(false)
				}
				if(data === 'vertically') return setMirrorV(!mirrorV)
				setMirrorH(!mirrorH)
			break;

			case "background":
				setInternalBg(data)
			break;

			case "draw":
				socketActions.updateSettings({
					output_draw_on_screen: settings?.output_draw_on_screen === "true" ? "false" : "true",
				});
			break;

			default:
				break;
		}
	};

	if (!cloud && !selectedProject) {
		return null;
	} else {
		return (
			<DocumentMeta {...meta}>
				<Menu id={"output"} theme="dark" animation="fade">
					<Item id="save_png" onClick={handleItemClick}>
						Save as PNG
					</Item>
					<Item id="mute" onClick={handleItemClick}>
						<p className="mr-auto">Mute all audio</p>
						{forceMute && 
							<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5">
								<path d="M9.547 3.062A.75.75 0 0110 3.75v12.5a.75.75 0 01-1.264.546L4.703 13H3.167a.75.75 0 01-.7-.48A6.985 6.985 0 012 10c0-.887.165-1.737.468-2.52a.75.75 0 01.7-.48h1.535l4.033-3.796a.75.75 0 01.811-.142zM13.28 7.22a.75.75 0 10-1.06 1.06L13.94 10l-1.72 1.72a.75.75 0 001.06 1.06L15 11.06l1.72 1.72a.75.75 0 101.06-1.06L16.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L15 8.94l-1.72-1.72z" />
							</svg>
						}
					</Item>
					<Item id="draw" onClick={handleItemClick}>
						<p className="mr-auto">Draw on screen</p>
						{settings?.output_draw_on_screen === "true" && 
							<FontAwesomeIcon icon={faCheck} />
						}
					</Item>
					<Submenu label="Mirror">
						<Item id="mirror" data="vertically" onClick={handleItemClick}>
							<svg className="text-white" width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
							<path d="M16.7957 18.2666C17.0637 18.2666 17.2559 18.0083 17.1789 17.7516L16.8283 16.583C16.8132 16.5327 16.8081 16.4799 16.8134 16.4277C16.8187 16.3754 16.8343 16.3247 16.8592 16.2785C16.8841 16.2322 16.9179 16.1913 16.9586 16.1581C16.9993 16.1249 17.0461 16.1001 17.0965 16.085L17.735 15.8934C17.7853 15.8783 17.8381 15.8732 17.8904 15.8785C17.9426 15.8839 17.9933 15.8994 18.0396 15.9243C18.0858 15.9492 18.1267 15.983 18.1599 16.0237C18.1931 16.0644 18.218 16.1113 18.2331 16.1616L18.7381 17.8451C18.8039 18.0641 18.8175 18.2954 18.7779 18.5206C18.7383 18.7458 18.6466 18.9587 18.5101 19.1421C18.3736 19.3256 18.1961 19.4745 17.9918 19.5772C17.7875 19.6798 17.562 19.7333 17.3333 19.7332H15.2304C15.1243 19.7332 15.0226 19.6911 14.9475 19.6161C14.8725 19.5411 14.8304 19.4393 14.8304 19.3332V18.6666C14.8304 18.5605 14.8725 18.4587 14.9475 18.3837C15.0226 18.3087 15.1243 18.2666 15.2304 18.2666H16.7957ZM12.1697 19.7332C12.2757 19.7332 12.3775 19.6911 12.4525 19.6161C12.5275 19.5411 12.5697 19.4393 12.5697 19.3332V18.6666C12.5697 18.5605 12.5275 18.4587 12.4525 18.3837C12.3775 18.3087 12.2757 18.2666 12.1697 18.2666H7.96372C7.85763 18.2666 7.75589 18.3087 7.68087 18.3837C7.60586 18.4587 7.56372 18.5605 7.56372 18.6666V19.3332C7.56372 19.4393 7.60586 19.5411 7.68087 19.6161C7.75589 19.6911 7.85763 19.7332 7.96372 19.7332H12.1697ZM4.90298 19.7332C5.00907 19.7332 5.11081 19.6911 5.18583 19.6161C5.26084 19.5411 5.30298 19.4393 5.30298 19.3332V18.6666C5.30298 18.5605 5.26084 18.4587 5.18583 18.3837C5.11081 18.3087 5.00907 18.2666 4.90298 18.2666H3.33765C3.06965 18.2666 2.87752 18.0083 2.95445 17.7516L3.30512 16.583C3.32021 16.5327 3.32524 16.4799 3.31993 16.4277C3.31461 16.3754 3.29906 16.3247 3.27415 16.2784C3.24923 16.2322 3.21546 16.1913 3.17474 16.1581C3.13403 16.1249 3.08717 16.1001 3.03685 16.085L2.39832 15.8934C2.348 15.8783 2.29521 15.8732 2.24295 15.8785C2.19069 15.8839 2.13999 15.8994 2.09374 15.9243C2.04749 15.9492 2.0066 15.983 1.9734 16.0237C1.9402 16.0644 1.91534 16.1113 1.90025 16.1616L1.39518 17.8451C1.32948 18.0641 1.31589 18.2954 1.35549 18.5206C1.3951 18.7458 1.4868 18.9586 1.62328 19.1421C1.75977 19.3255 1.93725 19.4745 2.14155 19.5771C2.34586 19.6798 2.57134 19.7332 2.79998 19.7332H4.90298ZM2.69012 13.5286C2.67502 13.5789 2.66999 13.6317 2.6753 13.684C2.68061 13.7363 2.69616 13.787 2.72106 13.8332C2.74597 13.8795 2.77974 13.9204 2.82045 13.9536C2.86115 13.9868 2.908 14.0116 2.95832 14.0267L3.59685 14.2183C3.64716 14.2334 3.69996 14.2384 3.75222 14.2331C3.80448 14.2278 3.85518 14.2123 3.90143 14.1874C3.94768 14.1624 3.98857 14.1287 4.02177 14.088C4.05497 14.0473 4.07982 14.0004 4.09492 13.9501L4.44552 12.7815C4.52252 12.5248 4.33032 12.2666 4.06238 12.2666H3.36638C3.28036 12.2666 3.19663 12.2943 3.12761 12.3457C3.05859 12.397 3.00797 12.4692 2.98325 12.5516L2.69012 13.5286ZM16.767 12.2666C16.853 12.2666 16.9367 12.2943 17.0058 12.3457C17.0748 12.397 17.1254 12.4692 17.1501 12.5516L17.4432 13.5286C17.4583 13.5789 17.4633 13.6317 17.458 13.684C17.4527 13.7363 17.4371 13.787 17.4122 13.8332C17.3873 13.8795 17.3536 13.9204 17.3129 13.9536C17.2721 13.9868 17.2253 14.0116 17.175 14.0267L16.5365 14.2183C16.4861 14.2334 16.4333 14.2384 16.3811 14.2331C16.3288 14.2278 16.2781 14.2123 16.2319 14.1874C16.1856 14.1624 16.1447 14.1287 16.1115 14.088C16.0783 14.0473 16.0535 14.0004 16.0384 13.9501L15.6878 12.7815C15.6109 12.5248 15.803 12.2666 16.0709 12.2666H16.767ZM7.99998 9.73324C7.99998 9.62715 8.04213 9.52541 8.11714 9.45039C8.19216 9.37538 8.2939 9.33323 8.39998 9.33323H11.7333C11.8394 9.33323 11.9411 9.37538 12.0162 9.45039C12.0912 9.52541 12.1333 9.62715 12.1333 9.73324V10.3999C12.1333 10.506 12.0912 10.6077 12.0162 10.6827C11.9411 10.7578 11.8394 10.7999 11.7333 10.7999H8.39998C8.2939 10.7999 8.19216 10.7578 8.11714 10.6827C8.04213 10.6077 7.99998 10.506 7.99998 10.3999V9.73324ZM14 9.73324C14 9.62715 14.0421 9.52541 14.1171 9.45039C14.1922 9.37538 14.2939 9.33323 14.4 9.33323H18.6667C18.7727 9.33323 18.8745 9.37538 18.9495 9.45039C19.0245 9.52541 19.0667 9.62715 19.0667 9.73324V10.3999C19.0667 10.506 19.0245 10.6077 18.9495 10.6827C18.8745 10.7578 18.7727 10.7999 18.6667 10.7999H14.4C14.2939 10.7999 14.1922 10.7578 14.1171 10.6827C14.0421 10.6077 14 10.506 14 10.3999V9.73324ZM1.06665 9.73324C1.06665 9.62715 1.10879 9.52541 1.18381 9.45039C1.25882 9.37538 1.36056 9.33323 1.46665 9.33323H5.73332C5.8394 9.33323 5.94115 9.37538 6.01616 9.45039C6.09117 9.52541 6.13332 9.62715 6.13332 9.73324V10.3999C6.13332 10.506 6.09117 10.6077 6.01616 10.6827C5.94115 10.7578 5.8394 10.7999 5.73332 10.7999H1.46665C1.36056 10.7999 1.25882 10.7578 1.18381 10.6827C1.10879 10.6077 1.06665 10.506 1.06665 10.3999V9.73324ZM3.36638 7.86657C3.28036 7.86657 3.19663 7.83884 3.12761 7.78748C3.05859 7.73613 3.00797 7.6639 2.98325 7.5815L1.39518 2.28804C1.32948 2.06904 1.31589 1.83771 1.35549 1.61252C1.3951 1.38733 1.4868 1.17452 1.62328 0.991083C1.75977 0.807642 1.93725 0.658651 2.14155 0.556006C2.34586 0.45336 2.57134 0.399903 2.79998 0.399902H17.3333C17.562 0.399917 17.7874 0.453383 17.9917 0.556033C18.196 0.658683 18.3735 0.807673 18.51 0.991109C18.6465 1.17455 18.7382 1.38735 18.7778 1.61253C18.8174 1.83771 18.8038 2.06904 18.7381 2.28804L17.1501 7.5815C17.1254 7.66389 17.0748 7.73612 17.0058 7.78747C16.9367 7.83882 16.853 7.86656 16.767 7.86657H16.0709C15.803 7.86657 15.6109 7.6083 15.6878 7.35164L17.1789 2.3815C17.2559 2.12484 17.0637 1.86657 16.7957 1.86657H3.33758C3.06965 1.86657 2.87752 2.12484 2.95445 2.3815L4.44552 7.35164C4.52252 7.6083 4.33032 7.86657 4.06238 7.86657H3.36638Z" fill="currentColor"/>
							</svg>
							<p className="pl-2">Mirror horizontally</p>
						</Item>
						<Item id="mirror" data="horizontally" onClick={handleItemClick}>
							<svg className="text-white transform rotate-90" width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
								<path d="M16.7957 18.2666C17.0637 18.2666 17.2559 18.0083 17.1789 17.7516L16.8283 16.583C16.8132 16.5327 16.8081 16.4799 16.8134 16.4277C16.8187 16.3754 16.8343 16.3247 16.8592 16.2785C16.8841 16.2322 16.9179 16.1913 16.9586 16.1581C16.9993 16.1249 17.0461 16.1001 17.0965 16.085L17.735 15.8934C17.7853 15.8783 17.8381 15.8732 17.8904 15.8785C17.9426 15.8839 17.9933 15.8994 18.0396 15.9243C18.0858 15.9492 18.1267 15.983 18.1599 16.0237C18.1931 16.0644 18.218 16.1113 18.2331 16.1616L18.7381 17.8451C18.8039 18.0641 18.8175 18.2954 18.7779 18.5206C18.7383 18.7458 18.6466 18.9587 18.5101 19.1421C18.3736 19.3256 18.1961 19.4745 17.9918 19.5772C17.7875 19.6798 17.562 19.7333 17.3333 19.7332H15.2304C15.1243 19.7332 15.0226 19.6911 14.9475 19.6161C14.8725 19.5411 14.8304 19.4393 14.8304 19.3332V18.6666C14.8304 18.5605 14.8725 18.4587 14.9475 18.3837C15.0226 18.3087 15.1243 18.2666 15.2304 18.2666H16.7957ZM12.1697 19.7332C12.2757 19.7332 12.3775 19.6911 12.4525 19.6161C12.5275 19.5411 12.5697 19.4393 12.5697 19.3332V18.6666C12.5697 18.5605 12.5275 18.4587 12.4525 18.3837C12.3775 18.3087 12.2757 18.2666 12.1697 18.2666H7.96372C7.85763 18.2666 7.75589 18.3087 7.68087 18.3837C7.60586 18.4587 7.56372 18.5605 7.56372 18.6666V19.3332C7.56372 19.4393 7.60586 19.5411 7.68087 19.6161C7.75589 19.6911 7.85763 19.7332 7.96372 19.7332H12.1697ZM4.90298 19.7332C5.00907 19.7332 5.11081 19.6911 5.18583 19.6161C5.26084 19.5411 5.30298 19.4393 5.30298 19.3332V18.6666C5.30298 18.5605 5.26084 18.4587 5.18583 18.3837C5.11081 18.3087 5.00907 18.2666 4.90298 18.2666H3.33765C3.06965 18.2666 2.87752 18.0083 2.95445 17.7516L3.30512 16.583C3.32021 16.5327 3.32524 16.4799 3.31993 16.4277C3.31461 16.3754 3.29906 16.3247 3.27415 16.2784C3.24923 16.2322 3.21546 16.1913 3.17474 16.1581C3.13403 16.1249 3.08717 16.1001 3.03685 16.085L2.39832 15.8934C2.348 15.8783 2.29521 15.8732 2.24295 15.8785C2.19069 15.8839 2.13999 15.8994 2.09374 15.9243C2.04749 15.9492 2.0066 15.983 1.9734 16.0237C1.9402 16.0644 1.91534 16.1113 1.90025 16.1616L1.39518 17.8451C1.32948 18.0641 1.31589 18.2954 1.35549 18.5206C1.3951 18.7458 1.4868 18.9586 1.62328 19.1421C1.75977 19.3255 1.93725 19.4745 2.14155 19.5771C2.34586 19.6798 2.57134 19.7332 2.79998 19.7332H4.90298ZM2.69012 13.5286C2.67502 13.5789 2.66999 13.6317 2.6753 13.684C2.68061 13.7363 2.69616 13.787 2.72106 13.8332C2.74597 13.8795 2.77974 13.9204 2.82045 13.9536C2.86115 13.9868 2.908 14.0116 2.95832 14.0267L3.59685 14.2183C3.64716 14.2334 3.69996 14.2384 3.75222 14.2331C3.80448 14.2278 3.85518 14.2123 3.90143 14.1874C3.94768 14.1624 3.98857 14.1287 4.02177 14.088C4.05497 14.0473 4.07982 14.0004 4.09492 13.9501L4.44552 12.7815C4.52252 12.5248 4.33032 12.2666 4.06238 12.2666H3.36638C3.28036 12.2666 3.19663 12.2943 3.12761 12.3457C3.05859 12.397 3.00797 12.4692 2.98325 12.5516L2.69012 13.5286ZM16.767 12.2666C16.853 12.2666 16.9367 12.2943 17.0058 12.3457C17.0748 12.397 17.1254 12.4692 17.1501 12.5516L17.4432 13.5286C17.4583 13.5789 17.4633 13.6317 17.458 13.684C17.4527 13.7363 17.4371 13.787 17.4122 13.8332C17.3873 13.8795 17.3536 13.9204 17.3129 13.9536C17.2721 13.9868 17.2253 14.0116 17.175 14.0267L16.5365 14.2183C16.4861 14.2334 16.4333 14.2384 16.3811 14.2331C16.3288 14.2278 16.2781 14.2123 16.2319 14.1874C16.1856 14.1624 16.1447 14.1287 16.1115 14.088C16.0783 14.0473 16.0535 14.0004 16.0384 13.9501L15.6878 12.7815C15.6109 12.5248 15.803 12.2666 16.0709 12.2666H16.767ZM7.99998 9.73324C7.99998 9.62715 8.04213 9.52541 8.11714 9.45039C8.19216 9.37538 8.2939 9.33323 8.39998 9.33323H11.7333C11.8394 9.33323 11.9411 9.37538 12.0162 9.45039C12.0912 9.52541 12.1333 9.62715 12.1333 9.73324V10.3999C12.1333 10.506 12.0912 10.6077 12.0162 10.6827C11.9411 10.7578 11.8394 10.7999 11.7333 10.7999H8.39998C8.2939 10.7999 8.19216 10.7578 8.11714 10.6827C8.04213 10.6077 7.99998 10.506 7.99998 10.3999V9.73324ZM14 9.73324C14 9.62715 14.0421 9.52541 14.1171 9.45039C14.1922 9.37538 14.2939 9.33323 14.4 9.33323H18.6667C18.7727 9.33323 18.8745 9.37538 18.9495 9.45039C19.0245 9.52541 19.0667 9.62715 19.0667 9.73324V10.3999C19.0667 10.506 19.0245 10.6077 18.9495 10.6827C18.8745 10.7578 18.7727 10.7999 18.6667 10.7999H14.4C14.2939 10.7999 14.1922 10.7578 14.1171 10.6827C14.0421 10.6077 14 10.506 14 10.3999V9.73324ZM1.06665 9.73324C1.06665 9.62715 1.10879 9.52541 1.18381 9.45039C1.25882 9.37538 1.36056 9.33323 1.46665 9.33323H5.73332C5.8394 9.33323 5.94115 9.37538 6.01616 9.45039C6.09117 9.52541 6.13332 9.62715 6.13332 9.73324V10.3999C6.13332 10.506 6.09117 10.6077 6.01616 10.6827C5.94115 10.7578 5.8394 10.7999 5.73332 10.7999H1.46665C1.36056 10.7999 1.25882 10.7578 1.18381 10.6827C1.10879 10.6077 1.06665 10.506 1.06665 10.3999V9.73324ZM3.36638 7.86657C3.28036 7.86657 3.19663 7.83884 3.12761 7.78748C3.05859 7.73613 3.00797 7.6639 2.98325 7.5815L1.39518 2.28804C1.32948 2.06904 1.31589 1.83771 1.35549 1.61252C1.3951 1.38733 1.4868 1.17452 1.62328 0.991083C1.75977 0.807642 1.93725 0.658651 2.14155 0.556006C2.34586 0.45336 2.57134 0.399903 2.79998 0.399902H17.3333C17.562 0.399917 17.7874 0.453383 17.9917 0.556033C18.196 0.658683 18.3735 0.807673 18.51 0.991109C18.6465 1.17455 18.7382 1.38735 18.7778 1.61253C18.8174 1.83771 18.8038 2.06904 18.7381 2.28804L17.1501 7.5815C17.1254 7.66389 17.0748 7.73612 17.0058 7.78747C16.9367 7.83882 16.853 7.86656 16.767 7.86657H16.0709C15.803 7.86657 15.6109 7.6083 15.6878 7.35164L17.1789 2.3815C17.2559 2.12484 17.0637 1.86657 16.7957 1.86657H3.33758C3.06965 1.86657 2.87752 2.12484 2.95445 2.3815L4.44552 7.35164C4.52252 7.6083 4.33032 7.86657 4.06238 7.86657H3.36638Z" fill="currentColor"/>
							</svg>
							<p className="pl-2">Mirror vertically</p>
						</Item>
						<Item id="mirror" data="reset" onClick={handleItemClick}>
						<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor" class="w-5 h-5">
							<path stroke-linecap="round" stroke-linejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99" />
						</svg>
							<p className="pl-2">Reset</p>
						</Item>
					</Submenu>
					<Submenu label="Background color">
						<Item id="background" data="red" onClick={handleItemClick}>
							<div className="w-4 h-4 rounded" style={{backgroundColor: 'red'}}></div>
							<p className="pl-2">Red</p>
						</Item>
						<Item id="background" data="lime" onClick={handleItemClick}>
							<div className="w-4 h-4 rounded" style={{backgroundColor: 'lime'}}></div>
							<p className="pl-2">Green</p>
						</Item>
						<Item id="background" data="blue" onClick={handleItemClick}>
							<div className="w-4 h-4 rounded" style={{backgroundColor: 'blue'}}></div>
							<p className="pl-2">Blue</p>
						</Item>
						<Item id="background" data="black" onClick={handleItemClick}>
							<div className="w-4 h-4 rounded" style={{backgroundColor: 'black'}}></div>
							<p className="pl-2">Black</p>
						</Item>
						<Item id="background" data="white" onClick={handleItemClick}>
							<div className="w-4 h-4 rounded" style={{backgroundColor: 'white'}}></div>
							<p className="pl-2">White</p>
						</Item>
						
					</Submenu>
					

				</Menu>
				<div
					ref={pngDownloadRef}
					className={`overflow-hidden 
								w-${width} h-${height}`}
					style={{
						backgroundColor:
							internalBg ||
							query.get("bg") ||
							settings?.output_background_colour ||
							"transparent",
						cursor: settings?.output_cursor_show || "auto",
						transform: `scaleX(${mirrorH ? '-1' : '1'}) scaleY(${mirrorV ? '-1' : '1'})`
					}}
					onContextMenu={showContextMenu}
					
				>
					
					<DrawOnScreen ref={pngDownloadRef} />
					{!cloud &&
						selectedProject.cues.map((cue) => {
							let outputNameFormat;

							// if (cue.status !== "onair") return null;

							if (parseInt(outputId) === 1) {
								outputNameFormat = "outputOne";
							} else if (parseInt(outputId) === 2) {
								outputNameFormat = "outputTwo";
							} else if (parseInt(outputId) === 3) {
								outputNameFormat = "outputThree";
							} else if (parseInt(outputId) === 4) {
								outputNameFormat = "outputFour";
							} else if (outputId === "utility") {
								outputNameFormat = "outputUtility";
							}

							if (outputId === 1 && cue[outputNameFormat] === undefined) {
								return <OutputCueTransition key={cue.id} cue={cue} forceMute={forceMute} />;
							}

							if (
								cue[outputNameFormat] === false ||
								cue[outputNameFormat] === undefined
							)
								return null;
							return <OutputCueTransition key={cue.id} cue={cue} forceMute={forceMute} />;
						})}
					{cloud &&
						cloudGraphics
							?.slice()
							?.sort((a, b) => {
								const aIndex = order.indexOf(a.id);
								const bIndex = order.indexOf(b.id);
								return aIndex - bIndex;
							})
							.map((cue) => {
								let outputNameFormat;

								if (parseInt(outputId) === 1) {
									outputNameFormat = "outputOne";
								} else if (parseInt(outputId) === 2) {
									outputNameFormat = "outputTwo";
								} else if (parseInt(outputId) === 3) {
									outputNameFormat = "outputThree";
								} else if (parseInt(outputId) === 4) {
									outputNameFormat = "outputFour";
								} else if (outputId === "utility") {
									outputNameFormat = "outputUtility";
								}

								if (outputId === 1 && cue[outputNameFormat] === undefined) {
									return (
										<OutputCueTransition key={cue.id} cue={cue} cloud={cloud} />
									);
								}

								if (
									cue[outputNameFormat] === false ||
									cue[outputNameFormat] === undefined
								)
									return null;
								return (
									<OutputCueTransition key={cue.id} cue={cue} cloud={cloud} />
								);
							})}
				</div>
				<div
					className="w-screen h-screen absolute inset-0"
					style={{
						zIndex: -1,
						backgroundImage:
							query.get("bg") === "alpha" ? `url(${transparentImage})` : "none",
						backgroundSize: "20px",
						opacity: "10%",
					}}
				></div>
			</DocumentMeta>
		);
	}
}
